import { Add, Close } from "@mui/icons-material";
import { Grid, IconButton, Tooltip, Typography } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxInputLabel from "components/UI/AcxInputLabel";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import React from "react";
import { useModuleScoreGroupingOptions } from "./useModuleScoreGroupingOptions";
import {
    isPendingCutPoint,
    MAX_NUMERIC_RANGE_CUT_POINTS,
    numericRangeGroupLimitMessage,
    rangesMustBeContinuousMessage,
    type NumericRangeDraft,
} from "../../../Grouping/groupByDraft";
import {
    nextBucketStart,
} from "../../../Grouping/GroupByTypes";
import { SignalsGroupByOptionLabels } from "../../../SignalsModels";
import ChartCardStore from "../../ChartCard/ChartCardStore";
import { configureInputLabelSx } from "./groupByOptionConfig";

type Props = {
    chartStore: ChartCardStore;
    /**
     * The grouping being configured. Owned by the coordinator, which derives it from the drawer's
     * vizOptions -- this component holds no grouping state of its own and re-renders with a new
     * object after every edit.
     */
    draft: NumericRangeDraft;
    /** Reports an edited draft upward. Stable identity, so effects can depend on it. */
    onDraftChange: (draft: NumericRangeDraft) => void;
    /** Set when the ranges cannot be saved and there is something to say about why. */
    validationMessage?: string;
};

/** A row of the range builder, derived from the boundary at `index`. */
type RangeRow = {
    index: number;
    /**
     * The first value this row covers: 0 for the first row, and otherwise where the boundary above
     * leaves off: that boundary itself for a continuous range, a whole unit past it for a discrete
     * one. Null when the row above is unfilled and there is nothing to follow from.
     */
    lowerBound: number | null;
    /** The boundary this row owns -- the only value the user types. */
    upperBound: number | null;
    /** True when this boundary does not exceed the one above, so the row ends before it starts. */
    hasContinuityError: boolean;
};

/**
 * How a unit is written in the editor: short beside the input where space is tight, long in the
 * final row's sentence. A unit with no entry is written as the catalog spells it, which reads
 * correctly for a plain noun and is the only sensible default for one we have not seen.
 */
const UNIT_LABELS: Record<string, { short: string; long: string }> = {
    minutes: { short: "min", long: "minutes" },
    percent: { short: "%", long: "percent" },
};

/**
 * Configures a numeric range grouping: where the boundaries between groups fall.
 *
 * ## Why only one value per row is editable
 *
 * The rows are a view of one list of boundaries, not a list of independent ranges. Each boundary is
 * the upper bound of its row and the lower bound of the next, so the user types it once and both
 * rows follow. That is what makes gaps and overlaps unrepresentable rather than merely validated
 * against: there is no pair of numbers that could disagree. Editing a boundary re-labels the row
 * below it, removing one closes the range over it, and the trailing unbounded row always reads from
 * whichever boundary is currently last.
 *
 * ## The one error a user can reach
 *
 * A boundary that does not exceed the one above it -- typing 5 where 2 was, above a row already
 * bounded at 5. That row would end before it starts, so the offending input is marked and Save is
 * blocked until it is fixed. An empty boundary is not an error, just unfinished: it blocks Save and
 * "+ Add Group" without saying anything, the same way a categorical dimension with nothing selected
 * does.
 */
export const NumericRangeGroupByEditor: React.FC<Props> = observer(
    ({ chartStore, draft, onDraftChange, validationMessage }) => {
        const { cutPoints } = draft;

        const catalogEntry = chartStore.groupingStore.catalogEntryFor(
            draft.option,
        );

        const unit = catalogEntry?.unit ?? "";
        const unitLabels = UNIT_LABELS[unit.trim().toLowerCase()] ?? {
            short: unit,
            long: unit,
        };

        // The top of this dimension's scale, where it has one. A range whose values stop at a known
        // value closes its last row on that value instead of running open-ended, so module score
        // reads "95 to 100 %" where call duration reads "20+ min".
        //
        // Read from the catalog rather than from the option, the same way the range mode is. The
        // backend decides which ceilings are tops worth naming and publishes only those as
        // scaleTop, so the editor never keeps its own list of which dimensions those are.
        const scaleTop = catalogEntry?.scaleTop ?? null;

        const { moduleOptions, modulesLoading } =
            useModuleScoreGroupingOptions(draft);
        const selectedModule = moduleOptions.find(
            (option) => option.id === draft.moduleId,
        );

        // Where a row starts is the one thing the two range modes disagree about: a continuous row
        // opens at the boundary below it, a discrete one a whole unit past it, so "0-5 messages" is
        // followed by "6-10 messages" rather than "5-10 messages". Everything else here is shared.
        //
        // Read from the catalog, which publishes the same mode the bucketing SQL is written from.
        // Continuous while the catalog is still loading, matching the backend's default.
        const mode =
            chartStore.groupingStore.catalogEntryFor(draft.option)
                ?.numericRangeMode ?? "continuous";

        const rows: RangeRow[] = cutPoints.map((upperBound, index) => {
            const boundaryAbove = index === 0 ? null : cutPoints[index - 1];

            return {
                index,
                lowerBound:
                    index === 0
                        ? 0
                        : boundaryAbove === null
                          ? null
                          : nextBucketStart(boundaryAbove, mode),
                upperBound,
                // Checked against the boundary above rather than against the row's own lower bound,
                // because the rule is about the boundaries the user types and is the same in both
                // modes: this one must exceed the one above it. A discrete row's lower bound is
                // already a unit past that boundary, so comparing against it would reject the
                // single-value row consecutive cut points legitimately describe: 5 then 6 is
                // "0-5" and "6-6". The first row has no boundary above and starts at zero.
                hasContinuityError:
                    upperBound !== null && upperBound <= (boundaryAbove ?? 0),
            };
        });

        // The trailing row reads from the last boundary the user has actually filled in, so adding a
        // group -- or clearing the last one -- leaves it showing the range it already described
        // rather than blanking while the new value is typed.
        const lastFilledCutPoint =
            [...cutPoints].reverse().find((cutPoint) => !isPendingCutPoint(cutPoint)) ??
            null;

        const hasUnfilledRow = cutPoints.some(isPendingCutPoint);
        const hasReachedGroupLimit =
            cutPoints.length >= MAX_NUMERIC_RANGE_CUT_POINTS;
        const addGroupDisabledReason = hasReachedGroupLimit
            ? numericRangeGroupLimitMessage
            : hasUnfilledRow
              ? "Enter a value for the new group first."
              : undefined;

        const writeCutPoints = (next: (number | null)[]) =>
            onDraftChange({ ...draft, cutPoints: next });

        const setCutPoint = (index: number, typed: string) => {
            // Boundaries are whole positive units, so only digits are taken from what was typed --
            // a minus sign or a decimal point cannot be entered rather than being entered and then
            // rejected. An emptied field returns the row to unfilled.
            const digits = typed.replace(/[^0-9]/g, "");
            const next = [...cutPoints];
            next[index] = digits.length
                ? Number.parseInt(digits, 10)
                : null;
            writeCutPoints(next);
        };

        const removeRow = (index: number) =>
            writeCutPoints(cutPoints.filter((_, position) => position !== index));

        const addGroup = () => writeCutPoints([...cutPoints, null]);

        const optionLabel = SignalsGroupByOptionLabels[draft.option];

        return (
            <Grid container direction="column" rowGap={0.75}>
                {draft.option === "ModuleScore" && (
                    <Grid item sx={{ marginBottom: 1 }}>
                        <AcxInputLabel
                            htmlFor={`${chartStore.id}-group-by-module`}
                            labelText="Module"
                            sx={configureInputLabelSx}
                        />
                        {/*
                         * Never defaulted, even to the only module on the tenant: a score means
                         * nothing without saying whose, so the user picks it. Changing it keeps the
                         * groups, which are percentages and mean the same thing on any module.
                         */}
                        <AcxSelectSingle
                            id={`${chartStore.id}-group-by-module`}
                            options={moduleOptions}
                            labelField="label"
                            valueField="id"
                            defaultValue={selectedModule}
                            isLoading={modulesLoading}
                            placeholder="Select Module..."
                            fullWidth
                            onChange={(option) =>
                                onDraftChange({
                                    ...draft,
                                    moduleId: option?.id,
                                })
                            }
                        />
                    </Grid>
                )}

                <Grid item>
                    <AcxInputLabel
                        htmlFor={`${chartStore.id}-group-by-range-0`}
                        labelText={`Define ${optionLabel} Groups`}
                        sx={configureInputLabelSx}
                    />
                </Grid>

                {rows.map((row) => (
                    <Grid
                        key={row.index}
                        container
                        item
                        columnGap={1}
                        flexWrap="nowrap"
                        alignItems="center"
                        sx={{
                            // The remove control is revealed on hover, per the design, but stays in
                            // the tab order and appears on focus -- hiding it outright would make
                            // rows removable only with a pointer.
                            "& .range-row-remove": {
                                opacity: 0,
                                transition: "opacity 120ms",
                            },
                            "&:hover .range-row-remove, & .range-row-remove:focus-within":
                                {
                                    opacity: 1,
                                },
                        }}
                    >
                        <Grid item xs={5}>
                            <AcxMainTextField
                                id={`${chartStore.id}-group-by-range-${row.index}-lower`}
                                type="text"
                                textContainerSx={{ margin: "0" }}
                                value={row.lowerBound ?? ""}
                                isDisabled
                                fullWidth
                            />
                        </Grid>
                        <Grid item>
                            <Typography variant="body2" color="text.secondary">
                                to
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <AcxMainTextField
                                id={`${chartStore.id}-group-by-range-${row.index}`}
                                type="text"
                                inputProps={{
                                    inputMode: "numeric",
                                    // The field's own error state is a red border and a warning
                                    // icon whose message appears on hover, neither of which a
                                    // screen reader announces, so the input says so itself.
                                    "aria-invalid": row.hasContinuityError,
                                }}
                                textContainerSx={{ margin: "0" }}
                                placeholderText={unitLabels.long}
                                // Wide enough for any boundary these dimensions have -- a call
                                // longer than 9999 minutes is not a range anyone is drawing -- and
                                // narrow enough that the field cannot be filled with digits.
                                maxLength={4}
                                value={row.upperBound ?? ""}
                                error={row.hasContinuityError}
                                errorHelper={rangesMustBeContinuousMessage}
                                endAdornment={
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ paddingRight: 1 }}
                                    >
                                        {unitLabels.short}
                                    </Typography>
                                }
                                onChange={(event) =>
                                    setCutPoint(row.index, event.target.value)
                                }
                                fullWidth
                            />
                        </Grid>
                        <Grid item className="range-row-remove">
                            <Tooltip title="Remove group">
                                <IconButton
                                    id={`${chartStore.id}-group-by-range-${row.index}-remove`}
                                    aria-label={`Remove group ${row.index + 1}`}
                                    size="small"
                                    onClick={() => removeRow(row.index)}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                ))}

                {lastFilledCutPoint !== null &&
                    (scaleTop === null ? (
                        <Grid item>
                            {/*
                             * Not an input: the open-ended group is whatever is left above the last
                             * boundary, so it has nothing of its own to edit and follows the row
                             * above.
                             */}
                            <Typography variant="body2" color="text.secondary">
                                {`${nextBucketStart(lastFilledCutPoint, mode)}+ ${unitLabels.long}`}
                            </Typography>
                        </Grid>
                    ) : (
                        /*
                         * A dimension whose values stop at a known top closes its last group on
                         * that top instead. Both ends are read-only for the same reason: the lower
                         * follows whichever boundary is currently last, and the upper is the top of
                         * the scale, which is not the user's to move.
                         */
                        <Grid
                            container
                            item
                            columnGap={1}
                            flexWrap="nowrap"
                            alignItems="center"
                        >
                            <Grid item xs={5}>
                                <AcxMainTextField
                                    id={`${chartStore.id}-group-by-range-last-lower`}
                                    type="text"
                                    textContainerSx={{ margin: "0" }}
                                    value={nextBucketStart(
                                        lastFilledCutPoint,
                                        mode,
                                    )}
                                    isDisabled
                                    fullWidth
                                />
                            </Grid>
                            <Grid item>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    to
                                </Typography>
                            </Grid>
                            <Grid item xs={5}>
                                <AcxMainTextField
                                    id={`${chartStore.id}-group-by-range-last-upper`}
                                    type="text"
                                    textContainerSx={{ margin: "0" }}
                                    value={scaleTop}
                                    isDisabled
                                    endAdornment={
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ paddingRight: 1 }}
                                        >
                                            {unitLabels.short}
                                        </Typography>
                                    }
                                    fullWidth
                                />
                            </Grid>
                            {/* Keeps the row's width in step with the removable ones above it. */}
                            <Grid item sx={{ visibility: "hidden" }}>
                                <IconButton size="small" disabled tabIndex={-1}>
                                    <Close fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}

                <Grid item>
                    <Tooltip title={addGroupDisabledReason ?? ""}>
                        {/* Wrapped so the tooltip still fires while the button is disabled. */}
                        <span>
                            <AcxButton
                                id={`${chartStore.id}-group-by-range-add`}
                                color="white"
                                startIcon={<Add />}
                                sx={(buttonTheme) => ({
                                    color: buttonTheme.palette.primary.main,
                                })}
                                disabled={!!addGroupDisabledReason}
                                onClick={addGroup}
                            >
                                Add Group
                            </AcxButton>
                        </span>
                    </Tooltip>
                </Grid>

                {validationMessage && (
                    <Grid item>
                        {/*
                         * The same message the offending row's own error carries. Repeated here
                         * because the rows are narrow enough that an inline error is easy to miss,
                         * and because the limit message belongs to the range as a whole rather than
                         * to any one row.
                         */}
                        <Typography variant="body2" color="error.main">
                            {validationMessage}
                        </Typography>
                    </Grid>
                )}
            </Grid>
        );
    },
);

export default NumericRangeGroupByEditor;
